import React, { ComponentProps, ChangeEvent, FormEvent, useState } from 'react'
import { useAuth } from 'contexts/AuthContext'
import { useRouter } from 'hooks/useRouter'
import { useToast } from 'contexts/ToastContext'
import { useTranslation } from 'hooks/useTranslation'
import { Button } from 'components/Button'
import { Link } from 'components/Link'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { LoginFormRoot, CreateAMealPlan, TabContainer, Tab } from './LoginForm.styles'

import { useSmsCode } from 'api/mutations/useSmsCode'
import { useLogin } from 'api/mutations/useLogin'
import { Hr } from '../../../../components/Hr'

type LoginFormOwnProps = ComponentProps<typeof LoginFormRoot>
export type LoginFormProps = LoginFormOwnProps & {
  forward?: string
  isExistingEmail?: boolean
}

export type errorObject = {
  email: string | undefined
  phoneNumber: string | undefined
}

export type ClientData = {
  clientTokenId: string
  token: string
  createdAt: Date
}

export const LoginForm = ({ forward, isExistingEmail }: LoginFormProps) => {
  const { t } = useTranslation()
  const { addToast, removeToast } = useToast()
  const { setAuth } = useAuth()
  const { smsCode } = useSmsCode()
  const { login } = useLogin()
  const router = useRouter({ clearQuery: true })

  const [selectPasswordOption, setSelectPasswordOption] = useState(1)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')

  const [formErrors, setFormErrors] = useState<errorObject>({
    phoneNumber: undefined,
    email: undefined,
  })

  const onPhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numbersOnly = e.target.value.replace(/\D/g, '')
    e.target.value = numbersOnly

    if (!e.target.value && selectPasswordOption === 2) {
      setFormErrors({
        ...formErrors,
        phoneNumber: 'Please enter a phone number',
      })
      setPhone(numbersOnly)
      return
    }
    if (e.target.value.length < 10) {
      setFormErrors({
        ...formErrors,
        phoneNumber: 'Please enter 10 digits',
      })
      setPhone(numbersOnly)
      return
    }
    setFormErrors({ ...formErrors, phoneNumber: undefined })
    setPhone(numbersOnly)
  }

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'email') setEmail(e.target.value.toLowerCase())
    if (e.target.name === 'password') setPassword(e.target.value)
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (selectPasswordOption === 1) {
      login(
        { email, password },
        {
          onSuccess: ({ clientId }) => {
            setAuth({ clientId, role: 'client' })
            router.replace(forward || '/user')
          },
          onError: () => {
            addToast({
              title: t('login:toast.error.title'),
              message: t('login:toast.error.message'),
              duration: 5000,
              tone: 'error',
              onClose: () => removeToast('login-error'),
              id: 'login-error',
            })
          },
        }
      )
    }
    if (selectPasswordOption === 2) {
      smsCode(
        { phoneNumber: phone },
        {
          onSuccess: () => {
            router.replace(forward || `/login/confirmation/`)
          },
          onError: () => {
            addToast({
              title: 'Oops! Something went wrong!',
              message: 'Failed to send your login link. Please try again.',
              duration: 5000,
              tone: 'error',
              onClose: () => removeToast('sms-code-error'),
              id: 'sms-code-error',
            })
          },
        }
      )
    }
  }

  return (
    <LoginFormRoot onSubmit={handleSubmit} justifyContent="start">
      <Stack direction="column" css={{ width: '100%' }}>
        <Stack direction="column" gap="5">
          <Text
            size={{ '@initial': 'h4', '@bp3': 'h2' }}
            as="h2"
            color="accent-black"
            align="center"
          >
            {isExistingEmail ? t('login:existing-login') : t('login:welcome-back')}
          </Text>

          <Stack direction="column" alignItems="flex-start" css={{ minWidth: '355px' }}>
            <Stack direction="row" alignItems="center" justifyContent="center" width="fullWidth">
              <TabContainer direction="row">
                <Tab
                  type="button"
                  active={selectPasswordOption === 1}
                  onClick={() => setSelectPasswordOption(1)}
                  aria-label="Use Password"
                >
                  {t('login:password')}
                </Tab>
                <Tab
                  type="button"
                  active={selectPasswordOption === 2}
                  onClick={() => setSelectPasswordOption(2)}
                  aria-label="Use Text Message"
                >
                  {t('login:text')}
                </Tab>
              </TabContainer>
            </Stack>
          </Stack>

          <Stack
            direction="column"
            style={{
              display: selectPasswordOption === 1 ? 'block' : 'none',
            }}
            alignItems="center"
            gap="4"
          >
            <Stack direction="column" gap="3">
              <Text size={{ '@initial': 'body' }} color="accent-black" align="center">
                {isExistingEmail
                  ? t('login:existing-login-description')
                  : t('login:login-description')}
              </Text>

              <Stack direction="column" gap="3">
                <TextInput
                  type="email"
                  label={t('login:email')}
                  name="email"
                  onChange={onInputChange}
                  removeDiacritics={true}
                />

                <TextInput
                  type="password"
                  label={t('login:password')}
                  name="password"
                  onChange={onInputChange}
                  removeDiacritics={true}
                />
              </Stack>
            </Stack>

            <Stack direction="column" alignItems="center" gap="2">
              <Link href="/auth/reset-password" passHref css={{ width: '100%' }}>
                <Text size="body" as="a" color="accent-black">
                  {t('login:forgot')}
                </Text>
              </Link>
            </Stack>
          </Stack>

          <Stack
            direction="column"
            style={{
              display: selectPasswordOption === 2 ? 'block' : 'none',
            }}
            gap="3"
          >
            <Stack direction="column" gap="3">
              <Text size={{ '@initial': 'body' }} color="accent-black">
                {t('login:provide-phone')}
              </Text>
              <Stack direction="column">
                <TextInput
                  type="phone"
                  label={t('login:phone')}
                  name="phone"
                  onChange={onPhoneChange}
                  removeDiacritics={true}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  maxLength={10}
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack direction="column" css={{ width: '100%' }}>
            <Button>{selectPasswordOption === 1 ? t('login:login') : t('login:send-code')}</Button>
          </Stack>
        </Stack>

        <Hr style={{ marginBottom: '32px', paddingTop: '40px' }} />
      </Stack>
      <Stack direction="column" alignItems="center" width="fullWidth" gap="2">
        <Text align="center">{t('login:new-to-wfr')}</Text>
        <Button variant="secondaryAlt" as="a" href="/meal-plan" fullWidth transparent>
          {t('login:create-a-meal-plan')}
        </Button>
      </Stack>
    </LoginFormRoot>
  )
}

LoginForm.displayName = 'LoginForm'
